// « Chercher un rapprochement » — the aggregate/split search of a card tap (operator).
//
// A tap that no system pairs may be one bar/restaurant bill paid as several lines (2 Lightspeed tickets for
// one tap), or one line with a small difference (29,00 € ticket, 28,00 € terminal). The search proposes the
// combinations of unmatched ledger card lines, close in time, whose sum is the tap. It only PROPOSES: the
// operator declares the match (fin_declare_tap_match), with the difference stated. Pure, no I/O.

import type { IsoDate, LedgerCardLine } from '@lemontaigu/pms-core';

export interface MatchCandidate {
  sourceIds: string[];
  ledgerCents: number;
  /** tap − ledger: 0 for an exact match; a small number for a difference to explain. */
  diffCents: number;
  /** The days of the lines, for the operator to judge the timing. */
  days: IsoDate[];
}

export interface SearchOptions {
  /** Lines within this many days of the tap are candidates. */
  dayWindow?: number;
  /** At most this many lines in one combination. */
  maxLines?: number;
  /** A combination is proposed when |tap − ledger| is within this (cents). 0 = exact only. */
  toleranceCents?: number;
  /** At most this many proposals. */
  limit?: number;
}

const DEFAULTS: Required<SearchOptions> = { dayWindow: 1, maxLines: 3, toleranceCents: 100, limit: 12 };

function dayDistance(a: IsoDate, b: IsoDate): number {
  return Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000;
}

/** The combinations of unmatched lines that match a tap. Exact matches first, then the smaller
 *  combinations, then the smaller difference. A line appears at most once per combination. */
export function findMatchCandidates(
  tapCents: number,
  tapDay: IsoDate,
  lines: readonly LedgerCardLine[],
  options: SearchOptions = {},
): MatchCandidate[] {
  const o = { ...DEFAULTS, ...options };
  // Unique lines within the window, largest first so the bound below prunes early.
  const seen = new Set<string>();
  const pool = lines
    .filter((l) => dayDistance(l.day, tapDay) <= o.dayWindow && l.amountCents > 0)
    .filter((l) => (seen.has(l.sourceId) ? false : (seen.add(l.sourceId), true)))
    .sort((a, b) => b.amountCents - a.amountCents);

  const out: MatchCandidate[] = [];
  const pick: LedgerCardLine[] = [];

  const visit = (start: number, sum: number) => {
    if (pick.length > 0) {
      const diff = tapCents - sum;
      if (Math.abs(diff) <= o.toleranceCents) {
        out.push({
          sourceIds: pick.map((p) => p.sourceId),
          ledgerCents: sum,
          diffCents: diff,
          days: pick.map((p) => p.day),
        });
      }
    }
    if (pick.length === o.maxLines) return;
    for (let i = start; i < pool.length; i++) {
      const next = pool[i];
      if (!next) continue;
      // Largest first: once the sum overshoots the tap by more than the tolerance, later lines only add.
      if (sum + next.amountCents > tapCents + o.toleranceCents) continue;
      pick.push(next);
      visit(i + 1, sum + next.amountCents);
      pick.pop();
    }
  };
  visit(0, 0);

  out.sort(
    (a, b) =>
      Math.abs(a.diffCents) - Math.abs(b.diffCents) ||
      a.sourceIds.length - b.sourceIds.length ||
      a.sourceIds.join().localeCompare(b.sourceIds.join()),
  );
  return out.slice(0, o.limit);
}
